import React from 'react';
import { REPORT } from '../print/PrintAtoms';
import { PORTFOLIO_TOKENS } from './tokens';
import type { PortfolioSiteLogistics } from '../../lib/portfolioApi';

// Compact logistics facts for a Crown Pine pitch site (A1, C). Shown beneath the
// row in the Inquiry-sites view. Drive times are real road routes; airport and
// grid figures are nearest-facility lookups. Site A2 borrows A1's (inherited).

const titleCase = (s: string | null): string =>
  s == null ? '—' : s.toLowerCase().replace(/\b\w/g, c => c.toUpperCase());

const miles = (n: number | null | undefined): string =>
  n == null ? '—' : `${Math.round(Number(n)).toLocaleString()} mi`;

const drive = (mi: number | null, min: number | null): string => {
  if (mi == null) return '—';
  const rounded = Math.round(mi);
  if (min == null) return `${rounded} mi`;
  const h = Math.floor(min / 60);
  const m = min % 60;
  const t = h > 0 ? `${h}h ${String(m).padStart(2, '0')}m` : `${m}m`;
  return `${rounded} mi · ${t}`;
};

const Section: React.FC<{ title: string; children: React.ReactNode }> = ({ title, children }) => (
  <div style={{ marginTop: 8 }}>
    <div style={{
      fontFamily: PORTFOLIO_TOKENS.mono, fontSize: 8, fontWeight: 700, letterSpacing: '0.12em',
      textTransform: 'uppercase', color: REPORT.tertiary, marginBottom: 3,
    }}>
      {title}
    </div>
    {children}
  </div>
);

const Row: React.FC<{ label: string; value: string; sub?: string }> = ({ label, value, sub }) => (
  <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, padding: '2px 0' }}>
    <span style={{ fontFamily: PORTFOLIO_TOKENS.mono, fontSize: 9.5, color: REPORT.secondary, flex: '0 0 42%' }}>
      {label}
    </span>
    <span style={{ flex: 1, minWidth: 0 }}>
      <span style={{ fontFamily: PORTFOLIO_TOKENS.mono, fontSize: 10.5, fontWeight: 700, color: REPORT.heading }}>
        {value}
      </span>
      {sub && (
        <span style={{ fontFamily: PORTFOLIO_TOKENS.mono, fontSize: 9, color: REPORT.tertiary, marginLeft: 6 }}>
          {sub}
        </span>
      )}
    </span>
  </div>
);

export const SiteLogisticsCard: React.FC<{ logistics: PortfolioSiteLogistics }> = ({ logistics: l }) => {
  const airports = (l.nearest_airports || []).filter(a => a && a.name);
  return (
    <div style={{
      border: `1px solid ${REPORT.rule}`, borderTop: 'none', background: REPORT.surface,
      padding: '8px 12px 10px', marginTop: -1,
    }}>
      {l.inherited && (
        <div style={{
          fontFamily: PORTFOLIO_TOKENS.mono, fontSize: 8.5, letterSpacing: '0.06em',
          color: REPORT.tertiary, textTransform: 'uppercase', marginBottom: 2,
        }}>
          Same access profile as Site A1 (contiguous)
        </div>
      )}

      <Section title="Drive">
        <Row label="Houston — IAH" value={drive(l.drive_iah_mi, l.drive_iah_min)} />
        <Row label="Lufkin (county seat)" value={drive(l.drive_lufkin_mi, l.drive_lufkin_min)} />
      </Section>

      <Section title="Air">
        <Row
          label="Private jet"
          value={`${titleCase(l.jet_airport_name)}${l.jet_runway_ft ? ` · ${l.jet_runway_ft.toLocaleString()} ft` : ''}`}
          sub={miles(l.jet_airport_mi)}
        />
        {l.commercial_airport_name && (
          <Row
            label="Large commercial"
            value={`${titleCase(l.commercial_airport_name)}${l.commercial_runway_ft ? ` · ${l.commercial_runway_ft.toLocaleString()} ft` : ''}`}
            sub={miles(l.commercial_airport_mi)}
          />
        )}
        {airports.length > 0 && (
          <div style={{
            fontFamily: PORTFOLIO_TOKENS.mono, fontSize: 9, color: REPORT.secondary,
            marginTop: 2, paddingTop: 3, borderTop: `1px dotted ${REPORT.rule}`, lineHeight: 1.6,
          }}>
            <span style={{ color: REPORT.tertiary }}>Closest fields: </span>
            {airports.map((a, i) => (
              <span key={i}>
                {i > 0 && <span style={{ color: REPORT.tertiary }}> · </span>}
                {titleCase(a.name)} <span style={{ color: REPORT.tertiary }}>
                  {Math.round(a.mi)} mi{a.runway_ft ? `, ${a.runway_ft.toLocaleString()} ft` : ''}
                </span>
              </span>
            ))}
          </div>
        )}
      </Section>

      <Section title="Grid">
        <Row
          label="Nearest substation"
          value={`${l.substation_name || '—'}${l.substation_kv ? ` · ${l.substation_kv} kV` : ''}`}
          sub={`${l.substation_operator ? `${l.substation_operator} · ` : ''}${miles(l.substation_mi)}`}
        />
        <Row
          label="Nearest line"
          value={l.transmission_kv ? `${l.transmission_kv} kV line` : '—'}
          sub={miles(l.transmission_mi)}
        />
      </Section>
    </div>
  );
};

export default SiteLogisticsCard;
